import Axios from "axios";
import { t } from "i18next";
import { livisAlert } from "./alert";
import { LIVIS_API_BASE_URL } from "../config";
import {
  getAlertStatus,
  getLivisToken,
  getLivisUserInfo,
  getRefreshToken,
  removeAlertStatus,
  resetLivisUserInfo,
  setLivisUserInfo,
} from "./storage";
import { getTermsStatusAPI } from "../services/terms";
import { logger } from "../services/logger";

let refreshTokenPromise: Promise<boolean> | null = null;
let suppress403Toast = false; // State to track suppression of 403 toasts
let previousURL = ""; // Declare previousURL properly

// Function to refresh the access token
const refreshLivisToken = async () => {
  const refresh_token = getRefreshToken();
  if (!refresh_token) {
    resetLivisUserInfo(); // Logout if no refresh token
    return false;
  }

  if (!refreshTokenPromise) {
    refreshTokenPromise = (async () => {
      try {
        const response = await fetch(
          `${LIVIS_API_BASE_URL}authorization/refresh_token/`,
          {
            headers: {
              Accept: "application/json",
              "Content-Type": "application/json",
            },
            method: "POST",
            body: JSON.stringify({ refresh_token }),
          }
        );

        if (response.status === 401) {
          resetLivisUserInfo();
          window.location.href = "/";
          refreshTokenPromise = null;
          return false;
        }

        const data = await response.json();
        const user_info = data;
        const currentUserInfo = JSON.parse(getLivisUserInfo() || "{}");
        const new_user_info = {
          ...currentUserInfo,
          access_token: user_info?.data?.access_token,
          refresh_token: user_info?.data?.refresh_token,
        };

        if (new_user_info) {
          setLivisUserInfo(JSON.stringify(new_user_info));
          refreshTokenPromise = null;
          return true;
        } else {
          resetLivisUserInfo();
          refreshTokenPromise = null;
          return false;
        }
      } catch (error) {
        console.error(error);
        refreshTokenPromise = null;
        return false;
      }
    })();
  }

  return refreshTokenPromise;
};

// Request interceptor to add Authorization headers
function authRequestInterceptor(config: any) {
  // Stamped for API_SUCCESS/API_FAILURE duration logging in the response
  // interceptors below.
  config.metadata = { startTime: performance.now() };

  const token = getLivisToken();
  if (token) {
    config.headers.authorization = `Bearer ${token}`;
  }
  config.headers.Accept = "application/json";

  // Only set Content-Type to application/json if not sending FormData
  if (!(config.data instanceof FormData)) {
    config.headers["Content-Type"] = "application/json";
  } else {
    // For FormData, we must delete Content-Type to let the browser set it with the boundary
    delete config.headers["Content-Type"];
  }

  return config;
}

// List of restricted URLs where no alerts should be shown
const restricted_url = [
  "parts/check_part_uniqueness/",
  "authorization/login/",
  "workstations/check_unique_workstation_name/",
  "usecase/check_usecase_uniqueness/",
  "parameters/get_all_parameters/",
];

export const axios = Axios.create({
  baseURL:
    window.location.pathname === "/app/operator/report" ||
    window.location.pathname === "/app/operator/home"
      ? "http://localhost:9000/"
      : LIVIS_API_BASE_URL,
});

// Adding request interceptor
axios.interceptors.request.use(authRequestInterceptor);

// Adding response interceptor
axios.interceptors.response.use(
  (response) => {
    suppress403Toast = false; // Reset suppression on any successful response

    const startTime = (response.config as any)?.metadata?.startTime;
    logger.apiSuccess({
      method: response.config?.method,
      url: response.config?.url,
      status: response.status,
      duration_ms: startTime ? Math.round(performance.now() - startTime) : undefined,
    });

    return response;
  },
  async (error) => {
    const startTime = error?.config?.metadata?.startTime;
    logger.apiFailure({
      method: error?.config?.method,
      url: error?.config?.url,
      status: error?.response?.status,
      message: error?.message,
      duration_ms: startTime ? Math.round(performance.now() - startTime) : undefined,
    });

    console.log(error, "======================================>");
    if (error?.message === "Network Error") {
      livisAlert(`${t(error?.message)}`, "error");
      return null;
    }

    const status = error?.response?.status;
    console.log(status, "======================================>");

    const err_msg = t(`${error?.response?.data?.message}`);
    const no_alert_status = getAlertStatus();
    const restrict_url = restricted_url.includes(error?.config?.url);

    const suppressAlerts = error?.config?.suppressAlerts;
    let shouldSuppress = false;
    if (suppressAlerts === true) {
      shouldSuppress = true;
    } else if (
      typeof suppressAlerts === "object" &&
      Array.isArray(suppressAlerts.statusCode) &&
      suppressAlerts.statusCode.includes(status)
    ) {
      shouldSuppress = true;
    }
    if (!shouldSuppress) {
      if (status === 401) {
        const access_token = await refreshLivisToken();
        if (access_token) {
          error.config.headers["authorization"] = `Bearer ${getLivisToken()}`;
          return axios(error.config);
        } else {
          return error.response;
        }
      }

      // Handle 403 errors with toast suppression logic
      if (status === 403) {
  const currentURL = window.location.pathname;
  if (previousURL !== currentURL) {
    suppress403Toast = false;
  }
  previousURL = currentURL;

  const isTermsRoute = currentURL === "/auth/terms-gate";
  const isTermsStatusCall = error?.config?.url?.includes("terms");

  if (!isTermsRoute && !isTermsStatusCall) {
    try {
      const res = await getTermsStatusAPI();
      const data = res?.data?.data;

      if (data) {
        const hasMajorTerms = data.terms_required === true && data.screen_type !== "none";
        const hasMinorUpdate = data.minor_update != null;

        if (hasMajorTerms || hasMinorUpdate) {
          try {
            const userInfo = JSON.parse(getLivisUserInfo() || "{}");
            if (hasMajorTerms) userInfo.terms_required = true;
            if (hasMinorUpdate) userInfo.minor_update = data.minor_update;
            setLivisUserInfo(JSON.stringify(userInfo));
          } catch { void 0; }

          suppress403Toast = true;
          window.location.replace("/auth/terms-gate");
          return error.response;
        }
      }
    } catch { void 0; }
  }

  if (!suppress403Toast) {
    livisAlert(
      `${t("User does not have permission to perform this action")}`,
      "warning"
    );
    suppress403Toast = true;
  }
} else if (status === 200) {
        suppress403Toast = false; // Reset suppression on a 200 status
      }

      if (!no_alert_status && !restrict_url) {
        console.log(status, "========================>");
        switch (status) {
          case 400: {
            livisAlert(err_msg || `${t("Something went wrong")}`, "error");
            break;
          }
          case 422: {
            livisAlert(
              err_msg ||
                `${t(
                  "Not enough credits available, please contact livis administrator"
                )}`,
              "error"
            );
            break;
          }
          // case 403:
          //   livisAlert(
          //     `${t("User does not have permission to perform this action")}`,
          //     "warning"
          //   );
            break;
          // case 404:
          //   livisAlert(`${t("API Not Found")}`, "error");
          //   break;
          case 429:
            livisAlert(`${t("Too many requests, Please try again after sometime.")}`, "error", 10000);
            return error.response;
          // case 500:
          //   livisAlert(`${t("Internal server error")}`, "error");
          //   break;
          // default:
          //   livisAlert(`${t("Unknown Error")}`, "error");
        }
      }
    }

    removeAlertStatus();
    return error.response;
  }
);
